const PRODUCT_SELECT_EVENT = 'shopify:product:select';

class GetInstantAccess extends HTMLElement {
  connectedCallback() {
    this.fallbackForm = this.querySelector('[data-get-instant-access-fallback-form]');
    this.button = this.querySelector('[data-get-instant-access-button]');
    this.buttonText = this.querySelector('[data-get-instant-access-button-text]');
    this.error = this.querySelector('[data-get-instant-access-error]');
    this.variantInput = this.querySelector('[data-get-instant-access-variant-id]');
    this.quantityInput = this.querySelector('[data-get-instant-access-quantity]');
    this.defaultButtonText = this.dataset.buttonText || this.buttonText?.textContent?.trim() || 'GET INSTANT ACCESS';
    this.loadingText = this.dataset.loadingText || 'Opening secure checkout\u2026';
    this.isLoading = false;
    this.pendingProductSelect = null;
    this.abortController = new AbortController();

    this.button?.addEventListener('click', this.handleActivation);
    this.fallbackForm?.addEventListener('submit', this.handleSubmit);

    const productScope = this.productScope;
    productScope?.addEventListener(PRODUCT_SELECT_EVENT, this.handleProductSelect, {
      signal: this.abortController.signal,
    });

    this.syncFromSourceForm();
    this.setupStickyPurchaseBar();
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.button?.removeEventListener('click', this.handleActivation);
    this.fallbackForm?.removeEventListener('submit', this.handleSubmit);
  }

  get productScope() {
    return this.closest('.shopify-section, dialog, product-card') || document;
  }

  get sourceForm() {
    const closestForm = this.closest('form');
    if (closestForm instanceof HTMLFormElement) return closestForm;

    const scope = this.productScope;
    const forms = scope?.querySelectorAll('product-form-component form[data-type="add-to-cart-form"], form[action*="/cart/add"]');

    return (
      Array.from(forms || []).find((form) => form instanceof HTMLFormElement && !this.contains(form)) ||
      this.fallbackForm
    );
  }

  get localeRoot() {
    const shopifyRoot = window.Shopify?.routes?.root || '/';
    return shopifyRoot.endsWith('/') ? shopifyRoot : `${shopifyRoot}/`;
  }

  get addUrl() {
    return `${this.localeRoot}cart/add.js`;
  }

  get checkoutUrl() {
    return `${this.localeRoot}checkout`;
  }

  handleProductSelect = (event) => {
    if (!(event.target instanceof Element) || event.target.closest('product-card')) return;

    const variantId = event.detail?.variantId;
    if (variantId && this.variantInput instanceof HTMLInputElement) {
      this.variantInput.value = variantId;
    }

    const productSelectPromise = event.promise
      ?.then(({ variant, detail }) => {
        if (detail?.productId && this.dataset.productId && detail.productId !== this.dataset.productId) return;

        if (detail?.newProduct?.id) {
          this.dataset.productId = detail.newProduct.id;
        }

        const selectedVariant = detail?.resource || variant;
        const selectedVariantId = selectedVariant?.id;
        const selectedVariantAvailable =
          selectedVariant?.available ?? selectedVariant?.availableForSale ?? Boolean(selectedVariantId);

        if (this.variantInput instanceof HTMLInputElement) {
          this.variantInput.value = selectedVariantId || '';
        }

        this.setAvailability(Boolean(selectedVariantId) && selectedVariantAvailable !== false);
        window.requestAnimationFrame(() => {
          this.syncFromSourceForm();
          this.setupStickyPurchaseBar();
        });
      })
      .catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[get-instant-access] Product select promise rejected:', error);
      });

    this.pendingProductSelect = productSelectPromise;
    productSelectPromise?.finally(() => {
      if (this.pendingProductSelect === productSelectPromise) {
        this.pendingProductSelect = null;
      }
    });
  };

  handleActivation = (event) => {
    event.preventDefault();
    this.openCheckout();
  };

  handleSubmit = (event) => {
    event.preventDefault();
    this.openCheckout();
  };

  async openCheckout() {
    if (this.isLoading || this.button?.disabled) return;

    this.clearError();
    this.setLoading(true);

    if (this.pendingProductSelect) {
      await this.pendingProductSelect;
    }

    this.syncFromSourceForm();

    const form = this.sourceForm;
    if (!(form instanceof HTMLFormElement)) {
      this.setLoading(false);
      this.showError('Checkout could not open. Please try again.');
      return;
    }

    if (!form.checkValidity()) {
      this.setLoading(false);
      form.reportValidity();
      return;
    }

    const formData = new FormData(form);
    const variantId = formData.get('id');

    if (!variantId) {
      this.setLoading(false);
      this.showError('Please choose an available option before checkout.');
      return;
    }

    try {
      const response = await fetch(this.addUrl, {
        method: 'POST',
        headers: {
          Accept: 'application/json',
        },
        body: formData,
        credentials: 'same-origin',
      });
      const result = await response.json().catch(() => ({}));

      if (!response.ok || result.status) {
        throw new Error(result.description || result.message || 'Checkout could not open. Please try again.');
      }

      window.location.assign(this.checkoutUrl);
    } catch (error) {
      this.setLoading(false);
      this.showError(error instanceof Error ? error.message : 'Checkout could not open. Please try again.');
    }
  }

  syncFromSourceForm() {
    const form = this.sourceForm;
    if (!(form instanceof HTMLFormElement)) return;

    const sourceVariantInput = form.elements.namedItem('id');
    const sourceQuantityInput = form.elements.namedItem('quantity');

    if (sourceVariantInput instanceof HTMLInputElement && this.variantInput instanceof HTMLInputElement) {
      this.variantInput.value = sourceVariantInput.value;
    }

    if (sourceQuantityInput instanceof HTMLInputElement && this.quantityInput instanceof HTMLInputElement) {
      this.quantityInput.value = sourceQuantityInput.value;
    }

    const sourceButton = form.querySelector('[ref="addToCartButton"], button[name="add"], button[type="submit"]');
    if (sourceButton instanceof HTMLButtonElement && !this.contains(sourceButton)) {
      this.setAvailability(!sourceButton.disabled && Boolean(sourceVariantInput?.value));
    }
  }

  setupStickyPurchaseBar() {
    const section = this.closest('.shopify-section');
    const sticky = section?.querySelector('sticky-add-to-cart');
    const stickyButton = sticky?.querySelector('[ref="addToCartButton"]');

    if (!(sticky instanceof HTMLElement) || !(stickyButton instanceof HTMLButtonElement)) return;

    sticky.dataset.softprintlabPurchaseMode = 'instant';

    const buttonLabel = stickyButton.querySelector('.add-to-cart-text__content span:first-child');
    if (buttonLabel) {
      buttonLabel.textContent = this.defaultButtonText;
    }

    stickyButton.disabled = this.button instanceof HTMLButtonElement ? this.button.disabled : stickyButton.disabled;

    if (sticky.dataset.softprintlabInstantBound === 'true') return;

    sticky.dataset.softprintlabInstantBound = 'true';
    stickyButton.addEventListener(
      'click',
      (event) => {
        if (sticky.dataset.softprintlabPurchaseMode !== 'instant') return;

        event.preventDefault();
        event.stopImmediatePropagation();

        const activeButton = section.querySelector('get-instant-access [data-get-instant-access-button]');
        if (activeButton instanceof HTMLButtonElement && !activeButton.disabled) {
          activeButton.click();
        }
      },
      true
    );
  }

  setLoading(isLoading) {
    this.isLoading = isLoading;

    if (this.button instanceof HTMLButtonElement) {
      this.button.disabled = isLoading;
      this.button.setAttribute('aria-busy', isLoading ? 'true' : 'false');
    }

    if (this.buttonText) {
      this.buttonText.textContent = isLoading ? this.loadingText : this.defaultButtonText;
    }

    this.setupStickyPurchaseBar();
  }

  setAvailability(isAvailable) {
    if (this.button instanceof HTMLButtonElement && !this.isLoading) {
      this.button.disabled = !isAvailable;
    }
  }

  clearError() {
    if (!this.error) return;

    this.error.textContent = '';
    this.error.hidden = true;
  }

  showError(message) {
    if (this.button instanceof HTMLButtonElement) {
      this.button.disabled = false;
      this.button.focus();
    }

    if (!this.error) return;

    this.error.textContent = message;
    this.error.hidden = false;
  }
}

if (!customElements.get('get-instant-access')) {
  customElements.define('get-instant-access', GetInstantAccess);
}
